import React, { useContext, useState } from 'react';
import { SINGLE_NAVIGATION_PATHS } from '../../constants/single-navigation-paths';

import { useAuth } from './AuthContext';
import { instance as axiosInstance, withHeaders } from '../util';

export type TSecureDataZone = {
    name: {
        realm: string;
        sdz: string;
    };
    realm: string;
};

interface ZoneContextProps {
    avaliableSecureDataZones: TSecureDataZone[];
    setAvaliableSecureDataZones: (secureDataZones: TSecureDataZone[]) => void;
    getSelectedDataZone: () => any;
    setSelectedDataZone: (env: TSecureDataZone) => void;
    loadAvailableDataZones: () => void;
    zonesLoading: boolean;
}

const ZoneContext = React.createContext<ZoneContextProps>({
    avaliableSecureDataZones: [],
    setAvaliableSecureDataZones: () => {},
    getSelectedDataZone: () => {},
    setSelectedDataZone: () => {},
    loadAvailableDataZones: () => {},
    zonesLoading: false,
});
type UseZoneReturnType = {
    avaliableSecureDataZones: TSecureDataZone[];
    setAvaliableSecureDataZones: (secureDataZones: TSecureDataZone[]) => void;
    getSelectedDataZone: () => any; // You should replace 'any' with the appropriate type
    setSelectedDataZone: (env: TSecureDataZone) => void;
    loadAvailableDataZones: () => void;
    zonesLoading: boolean;
};

const useZone = (): UseZoneReturnType => useContext(ZoneContext);

const ZoneContextProvider = ({ children }: any): React.JSX.Element => {
    const { getToken } = useAuth();
    const [zonesLoading, setZonesLoading] = useState<boolean>(false);

    const [avaliableSecureDataZones, setAvaliableSecureDataZones] = useState<
        TSecureDataZone[]
    >([]);

    const getSortedZones = (zones: TSecureDataZone[]): TSecureDataZone[] => {
        const sdzRealmOrder = ['DEV', 'STAGE', 'UAT', 'PROD'];

        const sortedZones = zones.toSorted((a, b) => {
            const sdzRealmComparison =
                sdzRealmOrder.indexOf(a.name.realm) -
                sdzRealmOrder.indexOf(b.name.realm);

            if (sdzRealmComparison !== 0) {
                return sdzRealmComparison;
            }

            return a.name.sdz
                .toLowerCase()
                .localeCompare(b.name.sdz.toLowerCase());
        });
        return sortedZones;
    };

    const getSelectedDataZone = (): void => {
        let env;
        const currentEnvString = localStorage.getItem('env');
        if (currentEnvString !== null) {
            return JSON.parse(currentEnvString);
        }

        return env;
    };

    const retrieveDataZones = async (): Promise<TSecureDataZone[]> => {
        // TODO: refactor this out once it is possible, the
        // BIQ_CUSTOMER_NAME and BIQ_SDZ_NAME shouldnt be used this way,
        // they really should be considered secrets
        // I hope that the secure data zones and customer name can be part of the login response or in JWT
        // if not we may have to do a call to get them
        const customer = localStorage.getItem('realm');
        const headers = {
            BIQ_CUSTOMER_NAME: customer,
            Authorization: `Bearer ${getToken()}`,
        };

        return axiosInstance()
            .get(`/directory/${customer}/available_sdzs`, withHeaders(headers))
            .then((response: any) => {
                const secureDataZones = response.data;
                const zones: TSecureDataZone[] = [];
                secureDataZones.forEach((zone: any) => {
                    const newZone = {
                        name: zone,
                        realm: customer ?? '',
                    };
                    zones.push(newZone);
                });

                return getSortedZones(zones);
            });
    };

    const loadAvailableDataZones = (): void => {
        setZonesLoading(true);
        retrieveDataZones()
            .then((result) => {
                setAvaliableSecureDataZones(result);
                setZonesLoading(false);
            })
            .catch(() => setZonesLoading(false));
    };

    const setSelectedDataZone = (env: TSecureDataZone): void => {
        localStorage.setItem('env', JSON.stringify(env.name));
        window.location.href = SINGLE_NAVIGATION_PATHS.DEFAULT_PATH;
    };

    return (
        <ZoneContext.Provider
            value={{
                avaliableSecureDataZones,
                setAvaliableSecureDataZones: (sdzs: TSecureDataZone[]) =>
                    setAvaliableSecureDataZones(sdzs),
                getSelectedDataZone: () => getSelectedDataZone(),
                setSelectedDataZone: (env: TSecureDataZone) =>
                    setSelectedDataZone(env),
                loadAvailableDataZones: () => loadAvailableDataZones(),
                zonesLoading: zonesLoading,
            }}
        >
            {children}
        </ZoneContext.Provider>
    );
};

export { ZoneContextProvider, useZone };
